// @givmo/app-charity — the organization deep link: the ONE address that opens a NAMED organization's
// console (CPA-BE29).
//
// ══ WHY IT EXISTS ═════════════════════════════════════════════════════════════════════════════
//
// The console has no org-scoped address. The active organization comes from per-user storage alone
// (`org/active-org-storage.ts`; the one writer is `setActiveCharityId` in `org/active-org-context.tsx`)
// and every route is flat (`/donations`, `/org`, …). So the claim-approved email had nothing to point
// at that lands a claimant INSIDE the approved charity's console: an admin already signed in and
// administering ANOTHER organization landed on the doorway, in the organization they had before, and
// had to find the new one themselves — the dead end Jonathan hit on the 2026-09-07 walkthrough.
//
// `/organizations/$charityId` (`ORGANIZATION_DEEP_LINK_PATH`, `console/console-routes.ts` — the
// backend mirrors the literal) is that address. Like the root, it is a DOORWAY, not a page: it paints
// nothing, decides, and replaces itself.
//
// ══ THE DECISION, IN ORDER ═════════════════════════════════════════════════════════════════════
//
//   1. REFRESH THE MEMBERSHIPS READ FIRST. Approval is asynchronous and lands in the reviewer's tab;
//      the cache (15s stale window) may predate it, and a decision on a stale list would send the
//      person the mail was written for to the doorway as a stranger. Each ENTRY (keyed by the
//      charity id) is a second observer of the ONE query definition (`membershipsQueryOptions()` —
//      never the bare generated factory, see its ⚠) with `refetchOnMount: 'always'`, and
//      `isFetchedAfterMount` is the fact it waits on: it flips only once the query answered — with
//      data or an error — AFTER the entry mounted. On a cold load the initial read IS that answer:
//      the entry subscribes before the provider does (child effects first), and the provider joins
//      the request in flight — one request, measured by the pins.
//   2. HOLDS IT → SELECT IT, through the context's own writer, then forward to the DOORWAY
//      (`DEFAULT_AUTHENTICATED_ROUTE`), which runs the ONE landing rule for the newly active
//      organization — its chosen default module, the class default, the apps page. No second literal.
//      Two steps across two renders, by construction: the selection commits first, the effect re-runs
//      with the target active, and only then does it navigate — so the doorway never computes the
//      OLD organization's landing. That step is `useEnterOrganization` (`org/enter-organization.ts`),
//      the ONE mechanism, shared with the claim wizard's entry (CPA-WEB52) — which lands a person who
//      opens the wizard for a charity they already administer the same way. An entry whose target is
//      ALREADY the active organization does not re-ask: the live list holds it by construction, and
//      the refresh exists to find a membership the cache does NOT hold.
//   3. DOES NOT HOLD IT → forward to the doorway, untouched and unremarked. A person who is not an
//      admin of that charity is NOT told to claim it (no redirect into the wizard, no copy), and G10:
//      nothing here states or implies that claiming or accepting terms is required to be paid.
//      `error` with no list in hand takes the same road — the doorway answers `/apps`, whose retryable
//      error state is the way back. A caller who administers NOTHING is never decided here: the
//      portal-entry gate owns `loaded-empty` (→ the class door, `/claim`, the org search — never the
//      wizard for this charity), and the probe stands aside.
//
// ══ WHY THE PROBE LIVES IN THE LAYOUT, ABOVE `<OrgScopedBoundary>` ═════════════════════════════
//
// The decision CHANGES the active organization, and the boundary re-keys — remounting everything
// under it — on every change of it, including the cold-load change from "no organization yet" to the
// first membership. A route component doing this work would lose its in-flight refresh to the very
// remount it causes (and to the cold-load one before it), and re-ask: two requests where one was
// promised. So the route's component paints nothing, and the authenticated layout mounts
// `OrganizationDeepLinkProbe` ONCE, beside `<MembershipsFreshness>`, handing it the deep link's
// `$charityId` from the route match; the probe renders one keyed entry per address and nothing when
// the address is not matched. The probe sits above the portal-entry gate too, which is why step 3
// spells out the states it leaves to the gate.
//
// Signed out, the authenticated layout's guard carries this address through `?redirect=` to
// `/login-home` and the doors honour it after sign-in through the ONE sanitizer (`@givmo/org-console`
// `sanitizeRedirectTarget`, which accepts a rooted same-origin path with a dynamic segment).
//
// ══ A DESTINATION (CCM-R1-SIGN-LINK-ORG) ═══════════════════════════════════════════════════════
//
// `?to=<path>` (`ORGANIZATION_DEEP_LINK_DESTINATION_PARAM`) names the page to open once the
// organization is selected: a signing email links `/organizations/<id>?to=/compliance/sign/<request
// id>`, so a person who belongs to two organizations, with the other one active, lands on the request
// in ITS organization instead of reading it under the wrong one ("Not found"). Step 2 forwards there
// in place of the doorway, after the same refresh and the same committed selection. Steps 1 and 3 are
// unchanged: a person who does not hold the organization goes to the doorway with the destination
// dropped (it names a page of an organization they can't open). A destination is followed only when
// it is a path in this console that is not itself a deep link and carries no control character
// (`organizationDeepLinkDestination`); anything else is ignored, never an error, and the
// organization's landing opens as before.
//
// Deliberately nothing is painted: the shell around the address has already been painted by the time
// the entry mounts (the gate keeps the full portal for a caller with a list or an error), and a frame
// of chrome that exists only to be replaced is a flash, not a screen. No new surface ⇒ nothing new to
// judge at 375px; the shell's own phone floor is the registered mobile-shell spec.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ORGANIZATIONS_ROUTE } from '../console/console-routes';
import { DEFAULT_AUTHENTICATED_ROUTE } from '../features/auth/auth-routes';
import { membershipsQueryOptions, useActiveOrg } from '../org/active-org-context';
import { useEnterOrganization } from '../org/enter-organization';
import { sanitizeRedirectTarget } from './redirect-target';

/**
 * Whether `text` holds an ASCII control character. A browser's URL parser strips a tab, a newline
 * or a carriage return wherever it stands, so `/\t/host` reads as `//host`: no control character is
 * part of a console path (the backend's builder refuses the same, cut 1006-4's review).
 */
function hasControlCharacter(text: string): boolean {
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * The page a deep link may open, or `undefined` for the organization's landing: a path in THIS
 * console (the ONE redirect sanitizer's rule: rooted, never `//` or `/\`), with no control
 * character, read raw or decoded, and never a deep link itself, so one organization's link can't
 * hand the person on into another organization.
 */
export function organizationDeepLinkDestination(value: unknown): string | undefined {
  const destination = sanitizeRedirectTarget(value);
  if (destination === undefined || hasControlCharacter(destination)) return undefined;
  // Read as the router matches it: decoded, and regardless of case.
  let pathname: string;
  try {
    pathname = decodeURIComponent(destination.split(/[?#]/, 1)[0] ?? '').toLowerCase();
  } catch {
    return undefined;
  }
  if (hasControlCharacter(pathname)) return undefined;
  if (pathname === ORGANIZATIONS_ROUTE || pathname.startsWith(`${ORGANIZATIONS_ROUTE}/`)) {
    return undefined;
  }
  return destination;
}

export interface OrganizationDeepLinkProbeProps {
  /** The matched deep link's `$charityId`, or `null` while the address is not the one matched. */
  charityId: string | null;
  /**
   * The matched deep link's raw `to`, vetted here (`organizationDeepLinkDestination`); absent, or
   * not a page this console may open, the organization's landing.
   */
  destination?: unknown;
}

/**
 * Mounted ONCE by the authenticated layout (see the header). Renders one entry per deep-link
 * address, keyed by the charity id and its destination, and nothing at every other address.
 */
export function OrganizationDeepLinkProbe({
  charityId,
  destination,
}: OrganizationDeepLinkProbeProps) {
  if (charityId === null) return null;
  const page = organizationDeepLinkDestination(destination);
  return (
    <OrganizationDeepLinkEntry
      key={`${charityId} ${page ?? ''}`}
      charityId={charityId}
      destination={page}
    />
  );
}

/** The deep-link route's component. Paints nothing — the probe above the boundary does the work. */
export function OrganizationDeepLinkPage() {
  return null;
}

function OrganizationDeepLinkEntry({
  charityId,
  destination,
}: {
  charityId: string;
  destination: string | undefined;
}) {
  const navigate = useNavigate();
  const { memberships, membershipsState, activeCharityId } = useActiveOrg();

  // Captured at ENTRY: a person whose active organization already is the target needs no refresh
  // to know the membership is held (step 2).
  const [alreadyActive] = useState(() => activeCharityId === charityId);

  const { isFetchedAfterMount } = useQuery({
    ...membershipsQueryOptions(),
    refetchOnMount: alreadyActive ? false : 'always',
    notifyOnChangeProps: ['isFetchedAfterMount'],
  });
  const refreshed = alreadyActive || isFetchedAfterMount;

  // Decided only once the refresh has answered, and never for a caller who administers nothing —
  // that is the gate's state, not this probe's (step 3).
  const decided =
    refreshed && membershipsState !== 'pending' && membershipsState !== 'loaded-empty';
  const holds = membershipsState === 'ready' && memberships.some((m) => m.charity_id === charityId);

  // Step 2 — select, then the doorway, or the page the link names: the ONE mechanism
  // (`org/enter-organization.ts`).
  useEnterOrganization(charityId, decided && holds, destination);

  // Step 3 — does not hold it: the doorway, untouched and unremarked.
  useEffect(() => {
    if (!decided || holds) return;
    void navigate({ to: DEFAULT_AUTHENTICATED_ROUTE, replace: true });
  }, [decided, holds, navigate]);

  return null;
}
